import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ProfitEntry,
  CountrySummary,
  PaymentMethodSummary,
  DailyProfit,
} from "../../../../services/profitTrackingService";
import { formatNumber } from "../utils/profitFormatters";

interface ChartsTabProps {
  allEntries: ProfitEntry[];
  dailyProfits: DailyProfit[];
  countrySummary: CountrySummary[];
  paymentMethodSummary: PaymentMethodSummary[];
}

const MONTH_LABELS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const PIE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

/**
 * Convertit un final_profit d'une entrée en équivalent XAF.
 * - CAD_TO_XAF : final_profit est déjà en XAF
 * - XAF_TO_CAD : final_profit est en CAD → multiplier par izi_rate
 */
const entryFinalProfitXaf = (e: ProfitEntry): number => {
  const fp = e.final_profit ?? 0;
  if (e.direction === "CAD_TO_XAF") return fp;
  const rate = e.izi_rate ?? 0;
  return fp * rate;
};

const entryFinalProfitCad = (e: ProfitEntry): number => e.final_profit_cad ?? 0;

const formatShort = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toFixed(0);
};

export const ChartsTab: React.FC<ChartsTabProps> = ({
  allEntries,
  dailyProfits,
  countrySummary,
  paymentMethodSummary,
}) => {
  // ───────────────────────────────────────────────────────
  // 1) Données mensuelles (Bén. CAD vs Bén. XAF, après dépenses)
  // ───────────────────────────────────────────────────────
  const monthlyData = useMemo(() => {
    if (!allEntries.length) return [];
    const buckets = new Map<
      string,
      { ben_cad: number; ben_xaf: number; capital_cad: number; expenses_xaf: number; ops: number }
    >();

    allEntries.forEach((e) => {
      if (!e.date) return;
      const d = new Date(e.date);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const cur = buckets.get(key) || { ben_cad: 0, ben_xaf: 0, capital_cad: 0, expenses_xaf: 0, ops: 0 };
      cur.ben_cad += entryFinalProfitCad(e);
      cur.ben_xaf += entryFinalProfitXaf(e);
      cur.ops += 1;
      buckets.set(key, cur);
    });

    return Array.from(buckets.entries())
      .map(([key, val]) => {
        const [year, month] = key.split("-");
        return {
          monthKey: key,
          label: `${MONTH_LABELS_FR[parseInt(month, 10) - 1]} ${year.slice(2)}`,
          ben_cad: parseFloat(val.ben_cad.toFixed(2)),
          ben_xaf: parseFloat(val.ben_xaf.toFixed(0)),
          ops: val.ops,
        };
      })
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [allEntries]);

  // ───────────────────────────────────────────────────────
  // 2) Évolution journalière (Bén. final CAD)
  // ───────────────────────────────────────────────────────
  const dailyChartData = useMemo(() => {
    if (!dailyProfits.length) return [];
    const buckets = new Map<string, { ben_cad: number; expenses: number; ops: number }>();
    dailyProfits.forEach((d) => {
      const cur = buckets.get(d.date) || { ben_cad: 0, expenses: 0, ops: 0 };
      cur.ben_cad += d.daily_final_profit_cad || 0;
      cur.expenses += d.daily_expenses || 0;
      cur.ops += d.operation_count || 0;
      buckets.set(d.date, cur);
    });
    return Array.from(buckets.entries())
      .map(([date, val]) => ({
        date,
        label: date.slice(5), // MM-DD
        ben_cad: parseFloat(val.ben_cad.toFixed(2)),
        expenses: parseFloat(val.expenses.toFixed(2)),
        ops: val.ops,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [dailyProfits]);

  // ───────────────────────────────────────────────────────
  // 3) Répartition par pays (Bén. final CAD)
  // ───────────────────────────────────────────────────────
  const countryPieData = useMemo(() => {
    return (countrySummary || [])
      .filter((c) => (c.total_final_profit_cad ?? 0) !== 0)
      .map((c) => ({
        name: c.country,
        value: parseFloat((c.total_final_profit_cad ?? 0).toFixed(2)),
      }));
  }, [countrySummary]);

  // ───────────────────────────────────────────────────────
  // 4) Par moyen de paiement (Bén. final CAD vs dépenses)
  // ───────────────────────────────────────────────────────
  const paymentBarData = useMemo(() => {
    return (paymentMethodSummary || []).map((p) => ({
      name: `${p.payment_method}${p.country ? ` (${p.country.slice(0, 3)})` : ""}`,
      ben_cad: parseFloat((p.total_final_profit_cad ?? 0).toFixed(2)),
      expenses: parseFloat((p.total_expenses ?? 0).toFixed(2)),
      ops: p.operation_count ?? 0,
    }));
  }, [paymentMethodSummary]);

  // KPIs synthèse
  const totals = useMemo(() => {
    const ben_cad = allEntries.reduce((s, e) => s + entryFinalProfitCad(e), 0);
    const ben_xaf = allEntries.reduce((s, e) => s + entryFinalProfitXaf(e), 0);
    return { ben_cad, ben_xaf, count: allEntries.length };
  }, [allEntries]);

  const hasData = allEntries.length > 0 || dailyProfits.length > 0;

  if (!hasData) {
    return (
      <div className="p-12 bg-white rounded-xl border text-center text-gray-500">
        Aucune donnée à afficher. Ajoutez des entrées pour visualiser les diagrammes.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white border rounded-xl">
          <p className="text-xs text-gray-500 uppercase">Bénéfice total CAD (après dépenses)</p>
          <p className={`text-2xl font-bold ${totals.ben_cad < 0 ? "text-red-600" : "text-emerald-600"}`}>
            {formatNumber(totals.ben_cad, 2)} CAD
          </p>
        </div>
        <div className="p-4 bg-white border rounded-xl">
          <p className="text-xs text-gray-500 uppercase">Bénéfice total XAF (Taux IZI)</p>
          <p className={`text-2xl font-bold ${totals.ben_xaf < 0 ? "text-red-600" : "text-blue-600"}`}>
            {formatNumber(totals.ben_xaf, 0)} XAF
          </p>
        </div>
        <div className="p-4 bg-white border rounded-xl">
          <p className="text-xs text-gray-500 uppercase">Opérations</p>
          <p className="text-2xl font-bold text-gray-800">{totals.count}</p>
        </div>
      </div>

      {/* 1) Bar chart mensuel */}
      <div className="p-6 bg-white rounded-xl border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Bénéfices mensuels (après dépenses)</h3>
          <p className="text-xs text-gray-500">
            Bén. CAD = somme de <code>final_profit_cad</code>. Bén. XAF calculé via le Taux IZI de chaque entrée.
            Valeurs négatives = dépenses supérieures aux bénéfices.
          </p>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={monthlyData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="cad" tick={{ fontSize: 11 }} tickFormatter={formatShort} />
            <YAxis yAxisId="xaf" orientation="right" tick={{ fontSize: 11 }} tickFormatter={formatShort} />
            <Tooltip
              formatter={(value: any, name: any) => {
                if (name === "Bén. CAD") return [`${formatNumber(value as number, 2)} CAD`, name];
                if (name === "Bén. XAF") return [`${formatNumber(value as number, 0)} XAF`, name];
                return [value, name];
              }}
            />
            <Legend />
            <Bar yAxisId="cad" dataKey="ben_cad" name="Bén. CAD" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="xaf" dataKey="ben_xaf" name="Bén. XAF" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 2 + 3 + 4 en grille */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 2) Line chart journalier */}
        <div className="p-6 bg-white rounded-xl border">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Évolution journalière</h3>
            <p className="text-xs text-gray-500">Bénéfice final CAD jour par jour (dépenses déduites)</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyChartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatShort} />
              <Tooltip formatter={(v: any) => [`${formatNumber(v as number, 2)} CAD`, "Bén. CAD"]} />
              <Legend />
              <Line type="monotone" dataKey="ben_cad" name="Bén. CAD" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              <Line
                type="monotone"
                dataKey="expenses"
                name="Dépenses (XAF)"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 3) Pie chart par pays */}
        <div className="p-6 bg-white rounded-xl border">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Répartition par pays</h3>
            <p className="text-xs text-gray-500">Bénéfice final CAD par pays</p>
          </div>
          {countryPieData.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-12">Aucune donnée pays</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={countryPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={95}
                  label={(entry: any) => `${entry.name}: ${formatShort(entry.value)}`}
                >
                  {countryPieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any) => [`${formatNumber(v as number, 2)} CAD`, "Bén. CAD"]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 4) Bar chart par moyen de paiement */}
        <div className="p-6 bg-white rounded-xl border lg:col-span-2">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Par moyen de paiement</h3>
            <p className="text-xs text-gray-500">Bénéfice CAD vs Dépenses (par méthode et pays)</p>
          </div>
          {paymentBarData.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-12">Aucune donnée moyen de paiement</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={paymentBarData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatShort} />
                <Tooltip
                  formatter={(v: any, name: any) => {
                    if (name === "Bén. CAD") return [`${formatNumber(v as number, 2)} CAD`, name];
                    return [`${formatNumber(v as number, 2)}`, name];
                  }}
                />
                <Legend />
                <Bar dataKey="ben_cad" name="Bén. CAD" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartsTab;
